/**
 * Basepaths Component
 */

import React from 'react';
import { useTheme } from 'styled-components';
import Base from './base';
import { BasepathsProps } from './basepaths.types';
import { StyledBaseSVG } from './basepaths.style';
import { homeBase, firstBase, secondBase, thirdBase, defaultBaseLabels } from './constants';
import COLORS from '../../style/universal.color';

const retrievefilledColor = (filledColor: string | undefined): string => filledColor || COLORS.outsYellow;

const retrieveStrokeColor = (strokeColor: string | undefined, isDarkMode: boolean | undefined): string => {
  if (strokeColor) {
    return strokeColor;
  }

  return isDarkMode ? COLORS.mlbGrey2 : COLORS.mlbGrey3;
};

const retrieveEmptyColor = (emptyColor: string | undefined, isDarkMode: boolean | undefined): string => {
  if (emptyColor) {
    return emptyColor;
  }

  return isDarkMode ? COLORS.mlbGrey7 : COLORS.mlbGrey4;
};

const Basepaths = ({
  home,
  first,
  second,
  third,
  width = 30,
  altText = 'Bases.',
  filledColor,
  strokeColor,
  emptyColor,
  strokeWidth = 1,
  handlers = {},
  showHome = false,
  enableHomeEvents = false,
  className,
  baseLabels = defaultBaseLabels,
  ariaLabel,
  rounded = true,
}: BasepathsProps) => {
  const { dark } = useTheme();

  const defaultViewBox = !rounded ? `0 0 24 17.25` : `0 0 24 16.25`;

  // Set the viewbox to fit tightly around the bases
  const viewBox: string = showHome ? `0 0 24 23` : defaultViewBox;

  const commonProps = {
    filledColor: retrievefilledColor(filledColor),
    emptyColor: retrieveEmptyColor(emptyColor, dark),
    strokeColor: retrieveStrokeColor(strokeColor, dark),
    strokeWidth,
    handlers,
    baseLabels,
  };

  const hasEvents = Object.keys(handlers).length > 0;

  return (
    <StyledBaseSVG
      className={className}
      width={width}
      role={hasEvents ? undefined : 'img'}
      xmlns="http://www.w3.org/2000/svg"
      viewBox={viewBox}
      {...(hasEvents && { tabIndex: -1 })}
      aria-label={ariaLabel || 'base'}
    >
      <title>{altText}</title>
      <Base
        filled={!!third}
        transform={`translate(${!rounded ? `5.25, 7.25` : `5, 7.25`}) rotate(-315)`}
        baseValue={thirdBase}
        rounded={rounded}
        {...commonProps}
      />
      <Base
        filled={!!second}
        transform={`translate(${!rounded ? `12, 0.75` : `12, 0.5`}) rotate(-315)`}
        baseValue={secondBase}
        rounded={rounded}
        {...commonProps}
      />
      <Base
        filled={!!first}
        transform={`translate(${!rounded ? `18.75, 7.25` : `19, 7.25`}) rotate(-315)`}
        baseValue={firstBase}
        rounded={rounded}
        {...commonProps}
      />
      {showHome && (
        <Base
          filled={!!home}
          transform={'translate(12, 14) rotate(-315)'}
          baseValue={homeBase}
          eventsEnabled={enableHomeEvents}
          {...commonProps}
        />
      )}
    </StyledBaseSVG>
  );
};

export default Basepaths;
